import React from 'react';
import { SkeletonBlock } from '@/components/general/skeletonBlock/SkeletonBlock';
import { RequestedInformation, VerificationInfo } from '@/types/types';
import { usePhrase } from '@/hooks';
import { Alert, Stack } from '@mui/material';
import { VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES } from '@/consts/phrases/verificationPhrases';
import { JSXFromString } from '@/utils';
import { isEmpty } from '@/utils/fp';

interface RequestedInfoMessagesProps {
    verificationInfo: VerificationInfo;
    requestToken: string;
}

export const RequestedInfoMessages = (props: RequestedInfoMessagesProps) => {
    const { requestedInfo } = props.verificationInfo;
    const identicationStage = 'identification';

    const sortedRequestedInfo = requestedInfo.sort((a, b) =>
        a !== identicationStage ? 1 : b !== identicationStage ? -1 : 0,
    );

    return (
        <Stack
            spacing={2}
            sx={{
                width: '100%',
            }}
        >
            {!isEmpty(sortedRequestedInfo) &&
                sortedRequestedInfo?.map((requestedInfoItem, index) => (
                    <RequestedInfoMessage
                        requestToken={props.requestToken}
                        key={`info-message-${index}`}
                        requestedInfo={requestedInfoItem}
                        verificationInfo={props.verificationInfo}
                    />
                ))}
        </Stack>
    );
};

type RequestedInfoMessageProps = RequestedInfoMessagesProps & {
    requestedInfo: RequestedInformation;
};

const RequestedInfoMessage = (props: RequestedInfoMessageProps) => {
    const { t } = usePhrase();
    const message = buildMessage(
        props.requestedInfo,
        props.verificationInfo,
        props.requestToken,
        t,
    );
    if (!message) return <></>;
    return <Alert severity="warning">{JSXFromString(message)}</Alert>;
};

function buildMessage(
    requestedInfo: RequestedInformation,
    verificationInfo: VerificationInfo,
    requestToken: string,
    t: ReturnType<typeof usePhrase>['t'],
): string | undefined {
    const { isAutoIdentityVerificationEnabled, isManualIdentityVerificationEnabled } =
        verificationInfo;

    const requestInfoPhraseId = requestedInfoToPhraseIdsMap.get(requestedInfo);

    if (requestedInfo === 'identification') {
        if (isAutoIdentityVerificationEnabled && !isManualIdentityVerificationEnabled) {
            const onlineIdentityText = t(
                VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES.IDENTITY_VERIFICATION_ONLINE,
                { asString: true },
            );
            return t(VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES.ONLINE_IDENTIFICATION, {
                replaceTextVars: [
                    `<a href="/identity/${requestToken}/start"><b>${onlineIdentityText}</b></a>`,
                ],
            }) as string;
        } else if (isManualIdentityVerificationEnabled) {
            return t(requestInfoPhraseId) as string;
        } else if (!isAutoIdentityVerificationEnabled && !isManualIdentityVerificationEnabled) {
            return undefined;
        }
    }

    if (requestedInfo === 'specialty' || requestedInfo === 'profession') {
        const phraseBoardCertifiedOrEligibility = t(
            VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES.BOARD_CERTIFIED_OR_ELIGIBILITY,
        ) as string;
        const phraseMedicalLicenseNumber = t(
            VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES.MEDICAL_LICENSE_NUMBER,
        ) as string;

        if (requestedInfo === 'specialty') {
            const specialty = t(
                verificationInfo.specialtyPhraseId ||
                    VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES.NONE,
            ) as string;
            return t(requestInfoPhraseId, {
                replaceTextVars: [
                    specialty,
                    phraseBoardCertifiedOrEligibility,
                    phraseMedicalLicenseNumber,
                ],
            }) as string;
        }

        return t(requestInfoPhraseId, {
            replaceTextVars: [phraseBoardCertifiedOrEligibility, phraseMedicalLicenseNumber],
        }) as string;
    }

    return requestInfoPhraseId ? (t(requestInfoPhraseId) as string) : undefined;
}

const requestedInfoToPhraseIdsMap = new Map<RequestedInformation, number>([
    ['specialty', VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES.SPECIALTY],
    ['location', VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES.LOCATION],
    ['medical-school', VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES.MEDICAL_SCHOOL],
    ['profession', VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES.PROFESSION],
    ['identification', VERIFICATION_REQUEST_INFO_MESSAGES_PHRASES.IDENTIFICATION],
]);

export const RequestedInfoMessagesSkeleton = () => <SkeletonBlock amount={2} height={128} />;
